import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Users, Waves, DumbbellIcon, Calendar, Scan, Shirt, UserPlus, Clock, Mail, ChevronRight, Sparkles, Star, Snowflake, Wind } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const benefits = [
  {
    icon: Users,
    title: 'Group Discount',
    description: 'Special rates for 10+ employees',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Calendar,
    title: 'Flexible Terms',
    description: 'Open term contract with easy management',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    icon: Building2,
    title: 'Corporate Support',
    description: 'Dedicated account management',
    color: 'from-teal-500/20 to-emerald-500/20'
  }
];

const features = [
  {
    icon: Waves,
    title: '25m Swimming Pool',
    description: 'Temperature-controlled pool for swimming and aqua therapy',
    details: ['25m length', 'Temperature controlled', 'Professional lifeguard', 'Lane swimming available']
  },
  {
    icon: Snowflake,
    title: 'Ice Bath Therapy',
    description: 'Cold immersion therapy at 4°C',
    details: ['4°C temperature', 'Reduces inflammation', 'Speeds recovery', 'Mental resilience']
  },
  {
    icon: Waves,
    title: 'Spa Pool',
    description: 'Therapeutic spa pool with massage jets',
    details: ['Hydrotherapy', 'Muscle recovery', 'Joint mobility', 'Stress relief']
  },
  {
    icon: Wind,
    title: 'Sauna & Steam Room',
    description: 'Traditional dry heat and steam therapy',
    details: ['80-90°C sauna', 'Steam room', 'Detoxification', 'Muscle relaxation']
  }
];

const additionalBenefits = [
  {
    icon: Scan,
    title: 'Body Analysis',
    description: 'Monthly Tanita body scan included',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Shirt,
    title: 'Towel Service',
    description: 'Complimentary towel hire',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    icon: UserPlus,
    title: 'Guest Passes',
    description: 'Bring a friend once per month',
    color: 'from-teal-500/20 to-emerald-500/20'
  },
  {
    icon: Clock,
    title: 'Flexible Contract',
    description: 'Open term agreement',
    color: 'from-emerald-500/20 to-blue-500/20'
  }
];

export default function CorporatePage() {
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);
  const [hoveredBenefit, setHoveredBenefit] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
            
            {/* Animated Background Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, delay: 1.5, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full blur-3xl"
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              {/* Promotional Badge */}
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                className="inline-block mb-6"
              >
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                  <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                      EXCLUSIVE CORPORATE OFFER
                    </span>
                    <Star className="w-5 h-5 text-yellow-400" />
                  </div>
                </div>
              </motion.div>

              <h1 className="text-7xl font-bold mb-6">CORPORATE MEMBERSHIPS</h1>
              <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto">
                Enhance your workplace wellness with exclusive corporate rates on our Gateway membership. Available for businesses with 10 or more employees.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2, duration: 0.8 }}
                    className="relative group cursor-pointer"
                    onMouseEnter={() => setHoveredBenefit(index)}
                    onMouseLeave={() => setHoveredBenefit(null)}
                  >
                    <motion.div
                      animate={{
                        scale: hoveredBenefit === index ? 1.05 : 1,
                      }}
                      transition={{ duration: 0.3 }}
                      className="relative"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                      <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 h-full">
                        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-full w-fit mb-4">
                          <benefit.icon className="w-6 h-6 text-blue-400" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                        <p className="text-gray-400">{benefit.description}</p>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                className="inline-block mb-6"
              >
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                  <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                      ALL-INCLUSIVE MEMBERSHIP
                    </span>
                    <Sparkles className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </motion.div>
              <h2 className="text-5xl font-bold mb-4">MEMBERSHIP INCLUSIONS</h2>
              <p className="text-xl text-gray-400">Everything included in our corporate Gateway membership</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedFeature(selectedFeature === index ? null : index)}
                >
                  <motion.div
                    animate={{
                      scale: selectedFeature === index ? 1.02 : 1,
                    }}
                    transition={{ duration: 0.3 }}
                    className="relative"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                    <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                      <div className="p-6">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-full">
                            <feature.icon className="w-6 h-6 text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold">{feature.title}</h3>
                            <p className="text-gray-400">{feature.description}</p>
                          </div>
                        </div>

                        <AnimatePresence>
                          {selectedFeature === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="grid grid-cols-2 gap-3 mt-4">
                                {feature.details.map((detail, i) => (
                                  <div
                                    key={i}
                                    className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-3 backdrop-blur-sm"
                                  >
                                    <span className="text-sm">{detail}</span>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {additionalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group"
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.02, 1],
                      opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-br ${benefit.color} rounded-2xl blur-xl`}
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 h-full">
                    <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-full w-fit mb-4">
                      <benefit.icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-gray-400">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden rounded-2xl"
            >
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.05, 1]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"
              />
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1.05, 1, 1.05]
                }}
                transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
                className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-full blur-3xl"
              />
              
              <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
                <h2 className="text-4xl font-bold mb-4">GET STARTED</h2>
                <p className="text-xl text-gray-400 mb-8">
                  Contact our Corporate Wellness Manager to discuss your company's needs and receive a customized quote.
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <motion.a
                    href="mailto:taylor@thefitnessportal.co.nz"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Mail className="w-5 h-5" />
                    <span>EMAIL US</span>
                    <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </motion.a>
                  <motion.a
                    href="#contact"
                    className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>CONTACT FORM</span>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}